<template>
  <div 
    data-section-name="consultation_option"
    class="highlight-new p-4 bg-green-50 rounded-lg border border-green-200 mt-6 consultation-section"
  >
    <div class="flex items-start space-x-3">
      <div class="mt-1">
        <input
          :checked="modelValue"
          @change="$emit('update:modelValue', !modelValue)"
          type="checkbox"
          id="consultation-option"
          :class="[
            customerType === 'customer' ? 'enhanced-checkbox-customer' : 'enhanced-checkbox',
            'h-5 w-5'
          ]"
        />
      </div>
      <label for="consultation-option" class="cursor-pointer">
        <span class="block font-medium text-gray-700 mb-1">Would you like to discuss your options?</span>
        <span class="text-gray-600 text-sm">
          Our team can review your current policy and discuss alternative coverage options that might better fit your needs or budget.
        </span>
      </label>
    </div>
    
    <!-- Consultation scheduling (only if selected) -->
    <div v-if="modelValue" class="mt-4 pt-4 border-t border-green-200">
      <h5 class="font-semibold mb-3 text-gray-700">Schedule a Consultation</h5>
      <div class="form-grid-2 gap-4">
        <FormField
          label="Preferred Date"
          field-id="callback-date-container"
          :error="errors?.callbackDate"
          required
        >
          <ResponsiveDateInput
            field-id="callback-date"
            :model-value="callbackTime.date"
            @update:model-value="updateCallbackTime('date', $event)"
            :min-date="today"
            :error="errors?.callbackDate"
            label=""
            :required="true"
            :showOptionalLabel="false"
            :showRequiredLabel="false"
            :userRole="customerType"
          />
        </FormField>
        
        <FormField
          label="Preferred Time"
          field-id="callback-time-container"
          :error="errors?.callbackTime"
          required
          :userRole="customerType"
        >
          <InputWithIcon 
            icon="mdi:clock-outline" 
            :disabled="false"
            :userRole="customerType"
            :hasError="!!errors?.callbackTime"
          >
            <template #default="{ hasIcon }">
              <select
                id="callback-time"
                :value="callbackTime.time"
                @change="updateCallbackTime('time', $event.target.value)"
                class="w-full h-full bg-transparent focus:outline-none appearance-none pr-8 select-with-custom-arrow"
                :class="{ 
                  'rounded-l-none': hasIcon, 
                  'rounded-l-md': !hasIcon,
                  'rounded-r-md': true 
                }"
              >
                <option value="" disabled>Select a time</option>
                <option value="morning">Morning (9am - 12pm)</option>
                <option value="afternoon">Afternoon (12pm - 5pm)</option>
                <option value="evening">Evening (5pm - 7pm)</option>
              </select>
            </template>
          </InputWithIcon>
        </FormField>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import FormField from '@/components/common/FormField.vue';
import ResponsiveDateInput from '@/components/common/ResponsiveDateInput.vue';
import InputWithIcon from '@/components/common/InputWithIcon.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  callbackTime: {
    type: Object,
    required: true
  },
  today: {
    type: String,
    required: true
  },
  customerType: {
    type: String,
    default: 'customer',
    validator: value => ['customer', 'lender'].includes(value)
  },
  errors: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue', 'update:callbackTime']);

const updateCallbackTime = (field, value) => {
  const updatedTime = { ...props.callbackTime, [field]: value };
  emit('update:callbackTime', updatedTime);
};
</script>

<style scoped>
.consultation-section {
  margin-top: 2rem !important;
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
}

.form-grid-2 {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.select-with-custom-arrow {
  @apply appearance-none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1.25em 1.25em;
}
</style> 